import { describeFareGaps } from "./flight-search";
import type { CityEstimate, Estimate } from "./types";

/** What a city's summary row says under its name: what is priced, and at most one problem. */
export interface CitySummary {
  /** Quiet facts: "Flights for 6 of 440", "Hotel · 110 venues". */
  facts: string[];
  /**
   * One note when something is left out, with the reason: "Fares aren't published this far
   * ahead yet", "No airport found for Napa", or "No fares for 2 routes" when the search found none.
   */
  warning: string | null;
}

/**
 * The one-line account of a city's estimate for the summary list, so the many per-route details
 * stay in the city's breakdown: how many travelers' flights are priced, where the hotel comes
 * from, and a single note for anything missing.
 * @param city - The city's estimate.
 * @param estimate - The whole estimate, for the headcount.
 * @param faresPriceable - Whether the dates allow a fare search at all.
 * @returns The facts and the note.
 * @throws Never.
 */
export const summarizeCity = (city: CityEstimate, estimate: Estimate, faresPriceable: boolean): CitySummary => {
  const { headcount } = estimate;
  const routes = city.flights.routes;
  const missing = routes.filter((route) => route.status === "missing" || route.status === "unchecked");
  const flights = !faresPriceable
    ? "Flights need new dates"
    : routes.length === 0
      ? "Flights need home cities"
      : city.flights.pricedPeople >= headcount
        ? "Flights for everyone"
        : `Flights for ${city.flights.pricedPeople.toLocaleString("en-US")} of ${headcount.toLocaleString("en-US")}`;
  const hotel =
    city.hotel.source === "proposals"
      ? "Hotel from your proposals"
      : city.hotel.source === "venues"
        ? `Hotel · ${city.hotel.venueCount} ${city.hotel.venueCount === 1 ? "venue" : "venues"}`
        : city.hotel.loading
          ? "Hotel loading"
          : "No hotel rate data";
  return {
    facts: [flights, hotel],
    warning: faresPriceable ? describeFareGaps(missing.map((route) => route.reason ?? "No flights found")) : null,
  };
};
